import React, { useState } from 'react';
import { StarIcon, CheckCircleIcon, PenIcon, SendIcon, XIcon } from 'lucide-react';
// Helper component for star ratings
function StarRating({
  rating
}: {
  rating: number;
}) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - Math.ceil(rating);
  return <div className="flex gap-0.5 mb-3" aria-label={`${rating} out of 5 stars`} data-id="element-539">
      {[...Array(fullStars)].map((_, i) => <StarIcon key={`full-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-540" />)}

      {hasHalfStar && <div className="relative w-4 h-4" data-id="element-541">
          <StarIcon className="absolute top-0 left-0 w-4 h-4 text-gray-300" data-id="element-542" />
          <div className="absolute top-0 left-0 w-2 overflow-hidden" data-id="element-543">
            <StarIcon className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-544" />
          </div>
        </div>}

      {[...Array(emptyStars)].map((_, i) => <StarIcon key={`empty-${i}`} className="w-4 h-4 text-gray-300" data-id="element-545" />)}
    </div>;
}
interface TestimonialData {
  id: number;
  name: string;
  initials: string;
  location: string;
  rating: number;
  quote: string;
  color1: string;
  color2: string;
}
const testimonials: TestimonialData[] = [{
  id: 1,
  name: 'Linda M.',
  initials: 'LM',
  location: 'Wisconsin',
  rating: 5,
  quote: 'My hydrangeas survived their first winter! The frost checklist is a lifesaver.',
  color1: '#B7E4C7',
  color2: '#74C69D'
}, {
  id: 2,
  name: 'Tom B.',
  initials: 'TB',
  location: 'Georgia',
  rating: 4.5,
  quote: 'Really practical advice. The summer protocol saved my vegetable garden during the August heat.',
  color1: '#D8F3DC',
  color2: '#52B788'
}, {
  id: 3,
  name: 'Patricia K.',
  initials: 'PK',
  location: 'Michigan',
  rating: 5,
  quote: "Worth every penny and then some. I've already recommended it to three neighbors.",
  color1: '#95D5B2',
  color2: '#40916C'
}, {
  id: 4,
  name: 'David W.',
  initials: 'DW',
  location: 'Arizona',
  rating: 4,
  quote: 'Good guide overall. The heat wave section was exactly what I needed for desert gardening.',
  color1: '#74C69D',
  color2: '#2D6A4F'
}, {
  id: 5,
  name: 'Susan H.',
  initials: 'SH',
  location: 'Virginia',
  rating: 5,
  quote: 'I went from killing every plant I touched to having the best garden on my street. No joke.',
  color1: '#52B788',
  color2: '#1B4332'
}, {
  id: 6,
  name: 'Mike C.',
  initials: 'MC',
  location: 'Minnesota',
  rating: 4.5,
  quote: 'The seasonal checklists alone are worth it. Finally have a system instead of guessing.',
  color1: '#40916C',
  color2: '#143326'
}, {
  id: 7,
  name: 'Barbara L.',
  initials: 'BL',
  location: 'Florida',
  rating: 5,
  quote: 'Saved my orchids during Hurricane season. The storm blueprint is genius.',
  color1: '#B7E4C7',
  color2: '#52B788'
}, {
  id: 8,
  name: 'Richard P.',
  initials: 'RP',
  location: 'New York',
  rating: 4,
  quote: 'Straightforward and practical. Wish I had this years ago before I lost my Japanese maple.',
  color1: '#D8F3DC',
  color2: '#74C69D'
}];
function TestimonialCard({
  data
}: {
  data: TestimonialData;
}) {
  return <div className="bg-cream rounded-xl p-5 shadow-sm border border-cream-dark min-w-[300px] md:min-w-[340px] flex-shrink-0 flex flex-col" data-id="element-546">
      <div className="flex items-center gap-3 mb-4" data-id="element-547">
        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center shadow-inner" style={{
        background: `linear-gradient(135deg, ${data.color1} 0%, ${data.color2} 100%)`
      }} data-id="element-548">
          <span className="text-forest-dark font-bold text-sm font-sans" data-id="element-549">
            {data.initials}
          </span>
        </div>
        <div data-id="element-550">
          <p className="font-sans font-semibold text-forest-dark text-sm leading-tight" data-id="element-551">
            {data.name}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5" data-id="element-552">
            <p className="font-sans text-gray-500 text-xs" data-id="element-553">{data.location}</p>
            <span className="text-gray-300 text-xs" data-id="element-554">•</span>
            <div className="flex items-center gap-0.5" data-id="element-555">
              <CheckCircleIcon className="w-3 h-3 text-forest" data-id="element-556" />
              <span className="text-forest text-[10px] font-semibold uppercase tracking-wider" data-id="element-557">
                Verified
              </span>
            </div>
          </div>
        </div>
      </div>

      <StarRating rating={data.rating} data-id="element-558" />

      <blockquote className="font-sans text-gray-700 leading-relaxed text-[15px] italic flex-1" data-id="element-559">
        "{data.quote}"
      </blockquote>
    </div>;
}
export function TestimonialCarousel() {
  const carouselItems = [...testimonials, ...testimonials];
  const [showForm, setShowForm] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewLocation, setReviewLocation] = useState('');
  const [selectedStars, setSelectedStars] = useState(5);
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = () => {
    if (reviewText.trim() && reviewName.trim()) {
      setSubmitted(true);
      setTimeout(() => {
        setShowForm(false);
        setSubmitted(false);
        setReviewText('');
        setReviewName('');
        setReviewLocation('');
        setSelectedStars(5);
      }, 3000);
    }
  };
  return <section className="bg-white py-12 md:py-16 overflow-hidden" data-id="element-560">
      <div className="max-w-5xl mx-auto px-5 mb-10" data-id="element-561">
        <h2 className="font-serif text-forest text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-562">
          What Real Gardeners Are Saying
        </h2>
        <p className="text-gray-500 text-center font-sans" data-id="element-563">
          Join 4,700+ homeowners protecting their gardens
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative w-full overflow-hidden group" data-id="element-564">
        <div className="absolute top-0 bottom-0 left-0 w-12 md:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" data-id="element-565" />
        <div className="absolute top-0 bottom-0 right-0 w-12 md:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" data-id="element-566" />
        <div className="flex gap-6 w-max animate-marquee px-6 hover:[animation-play-state:paused]" data-id="element-567">
          {carouselItems.map((item, index) => <TestimonialCard key={`${item.id}-${index}`} data={item} data-id="element-568" />)}
        </div>
      </div>

      {/* Write a Review Section */}
      <div className="max-w-lg mx-auto px-5 mt-12" data-id="element-569">
        {!showForm ? <div className="bg-cream-dark rounded-xl p-6 text-center border border-gray-200/50" data-id="element-570">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-571">
              <PenIcon className="w-5 h-5 text-forest" data-id="element-572" />
            </div>
            <h3 className="font-semibold text-forest font-sans mb-2" data-id="element-573">
              Share Your Garden Story
            </h3>
            <p className="text-gray-500 text-sm font-sans mb-5" data-id="element-574">
              Have you used The 365 Garden Shield? We'd love to hear about your
              results.
            </p>
            <button onClick={() => setShowForm(true)} className="bg-forest text-white rounded-lg py-2.5 px-6 text-sm font-semibold hover:bg-forest-dark transition-colors shadow-sm" data-id="element-575">
              Write a Review
            </button>
          </div> : <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg" data-id="element-576">
            {submitted ? <div className="text-center py-8" data-id="element-577">
                <div className="w-14 h-14 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-578">
                  <CheckCircleIcon className="w-7 h-7 text-forest" data-id="element-579" />
                </div>
                <h3 className="font-semibold text-forest font-sans text-lg mb-2" data-id="element-580">
                  Thank You! 🌿
                </h3>
                <p className="text-gray-500 text-sm font-sans" data-id="element-581">
                  Your review has been submitted and will appear shortly.
                </p>
              </div> : <>
                {/* Header */}
                <div className="flex items-center justify-between mb-5" data-id="element-582">
                  <h3 className="font-semibold text-forest font-sans text-lg" data-id="element-583">
                    Write Your Review
                  </h3>
                  <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors" data-id="element-584">
                    <XIcon className="w-4 h-4 text-gray-500" data-id="element-585" />
                  </button>
                </div>

                {/* Star Selector */}
                <div className="mb-4" data-id="element-586">
                  <p className="text-sm text-gray-500 font-sans mb-2" data-id="element-587">
                    Your Rating
                  </p>
                  <div className="flex gap-1" data-id="element-588">
                    {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setSelectedStars(star)} className="focus:outline-none transition-transform hover:scale-110" data-id="element-589">
                        <StarIcon className={`w-7 h-7 ${star <= selectedStars ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} data-id="element-590" />
                      </button>)}
                  </div>
                </div>

                {/* Name & Location */}
                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-591">
                  <div data-id="element-592">
                    <label className="text-sm text-gray-500 font-sans mb-1 block" data-id="element-593">
                      Your Name *
                    </label>
                    <input type="text" value={reviewName} onChange={e => setReviewName(e.target.value)} placeholder="e.g. Sarah M." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest placeholder:text-gray-400" data-id="element-594" />
                  </div>
                  <div data-id="element-595">
                    <label className="text-sm text-gray-500 font-sans mb-1 block" data-id="element-596">
                      Location
                    </label>
                    <input type="text" value={reviewLocation} onChange={e => setReviewLocation(e.target.value)} placeholder="e.g. Texas" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest placeholder:text-gray-400" data-id="element-597" />
                  </div>
                </div>

                {/* Review Text */}
                <div className="mb-5" data-id="element-598">
                  <label className="text-sm text-gray-500 font-sans mb-1 block" data-id="element-599">
                    Your Review *
                  </label>
                  <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Tell us how The 365 Garden Shield helped your garden..." rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest placeholder:text-gray-400 resize-none" data-id="element-600" />
                </div>

                {/* Submit */}
                <button onClick={handleSubmit} disabled={!reviewText.trim() || !reviewName.trim()} className="w-full bg-forest text-white rounded-lg py-3 px-6 text-sm font-semibold hover:bg-forest-dark transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-601">
                  <SendIcon className="w-4 h-4" data-id="element-602" />
                  Submit Review
                </button>
              </>}
          </div>}
      </div>
    </section>;
}